import type { AgentForm } from '../../../utilities';
import { PIPELINE_SETTINGS_DEFAULTS } from '../../../utilities/pipelineSettingsDefaults';

export type FallbackKind = 'stt' | 'tts';

export type FallbackSettingsBase = {
  attempt_timeout: number;
  retry_interval: number;
};

export type SttFallbackSettingsPayload = FallbackSettingsBase & {
  max_retry_per_stt: number;
};

export type TtsFallbackSettingsPayload = FallbackSettingsBase & {
  max_retry_per_tts: number;
};

export type FallbackSettingsPayload =
  | SttFallbackSettingsPayload
  | TtsFallbackSettingsPayload;

export const FALLBACK_FIELD_NAMES = {
  stt: {
    attempt_timeout: 'stt_fallback_attempt_timeout',
    max_retry: 'stt_fallback_max_retry_per_stt',
    retry_interval: 'stt_fallback_retry_interval',
  },
  tts: {
    attempt_timeout: 'tts_fallback_attempt_timeout',
    max_retry: 'tts_fallback_max_retry_per_tts',
    retry_interval: 'tts_fallback_retry_interval',
  },
} as const satisfies Record<
  FallbackKind,
  {
    attempt_timeout: keyof AgentForm;
    max_retry: keyof AgentForm;
    retry_interval: keyof AgentForm;
  }
>;

const toNum = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
};

const toInt = (v: unknown): number | undefined => {
  const n = toNum(v);
  return n !== undefined && Number.isInteger(n) ? n : undefined;
};

const getFormValues = (kind: FallbackKind, form: AgentForm) => {
  const keys = FALLBACK_FIELD_NAMES[kind];
  return {
    attempt_timeout: form[keys.attempt_timeout] as number | undefined,
    max_retry: form[keys.max_retry] as number | undefined,
    retry_interval: form[keys.retry_interval] as number | undefined,
  };
};

const readMaxRetryFromApi = (
  kind: FallbackKind,
  raw: FallbackSettingsPayload | null | undefined
): number | undefined => {
  if (!raw) return undefined;
  return kind === 'stt'
    ? (raw as SttFallbackSettingsPayload).max_retry_per_stt
    : (raw as TtsFallbackSettingsPayload).max_retry_per_tts;
};

export const fallbackToForm = (
  kind: FallbackKind,
  raw: FallbackSettingsPayload | null | undefined
): Pick<AgentForm, (typeof FALLBACK_FIELD_NAMES)[FallbackKind][keyof (typeof FALLBACK_FIELD_NAMES)[FallbackKind]]> => {
  const keys = FALLBACK_FIELD_NAMES[kind];
  const defaults = PIPELINE_SETTINGS_DEFAULTS.fallback;

  return {
    [keys.attempt_timeout]: raw?.attempt_timeout ?? defaults.attempt_timeout,
    [keys.max_retry]: readMaxRetryFromApi(kind, raw) ?? defaults.max_retry,
    [keys.retry_interval]: raw?.retry_interval ?? defaults.retry_interval,
  } as Pick<
    AgentForm,
    (typeof FALLBACK_FIELD_NAMES)[FallbackKind][keyof (typeof FALLBACK_FIELD_NAMES)[FallbackKind]]
  >;
};

export const buildFallbackSettings = (
  kind: FallbackKind,
  form: AgentForm
): SttFallbackSettingsPayload | TtsFallbackSettingsPayload => {
  const defaults = PIPELINE_SETTINGS_DEFAULTS.fallback;
  const values = getFormValues(kind, form);

  const base: FallbackSettingsBase = {
    attempt_timeout: toNum(values.attempt_timeout) ?? defaults.attempt_timeout,
    retry_interval: toNum(values.retry_interval) ?? defaults.retry_interval,
  };

  const maxRetry = toInt(values.max_retry) ?? defaults.max_retry;

  if (kind === 'stt') {
    return { ...base, max_retry_per_stt: maxRetry };
  }

  return { ...base, max_retry_per_tts: maxRetry };
};
